import { Link, useForm } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageContainer } from '@/components/PageContainer'
import { PageHeader } from '@/components/PageHeader'
import { FlashBanner } from '@/components/FlashBanner'
import { ErrorBanner } from '@/components/ErrorBanner'
import { FormField } from '@/components/FormField'
import { FieldError } from '@/components/FieldError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { ResultError } from '@/types/form'
import { Industry } from '@/types/industry.ts'

interface Props {
  error?: ResultError
  industries: Industry[]
}

export default function ProjectsNew ({ error, industries }: Props): React.ReactElement {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    description: '',
    industry_id: ''
  })

  function handleSubmit (e: React.SubmitEvent): void {
    e.preventDefault()
    post('/projects')
  }

  return (
    <AppLayout>
      <PageContainer maxWidth='640px'>
        <PageHeader
          title='新しいプロジェクト'
          backHref='/projects'
          backLabel='プロジェクト一覧'
        />

        <FlashBanner />
        <ErrorBanner message={error?.errors?.guest_limit} />

        <form onSubmit={handleSubmit}>
          <div className='bg-(--bp-surface) border border-(--bp-border) rounded-[10px]'>
            <div className='p-6'>
              <FormField label='プロジェクト名' required>
                <Input
                  type='text'
                  value={data.name}
                  onChange={(e) => setData('name', e.target.value)}
                  placeholder='例：EC注文管理システム刷新'
                />
                <FieldError message={errors.name ?? error?.errors?.name?.[0]} />
              </FormField>
              <FormField label='業種（任意）' hint='リスク評価の精度向上に使用します'>
                <select
                  className='w-full py-[9px] px-3 border border-(--bp-border) rounded-[7px] text-sm text-(--bp-ink) bg-(--bp-surface) outline-none transition-all focus:border-(--bp-accent) focus:shadow-[0_0_0_3px_var(--bp-accent-pale)]'
                  value={data.industry_id}
                  onChange={(e) => setData('industry_id', e.target.value)}
                >
                  <option value=''>選択してください</option>
                  {industries.map((industry, index) => (
                    <option key={industry.id} value={industry.id}>{industry.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label='説明（任意）'>
                <textarea
                  className='w-full py-[9px] px-3 border border-(--bp-border) rounded-[7px] text-sm text-(--bp-ink) bg-(--bp-surface) outline-none transition-all focus:border-(--bp-accent) focus:shadow-[0_0_0_3px_var(--bp-accent-pale)]'
                  style={{ minHeight: 90, resize: 'vertical' }}
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                  placeholder='プロジェクトの概要や目的を記載してください'
                />
              </FormField>
            </div>
            <div className='px-6 py-4 bg-(--bp-surface2) border-t border-(--bp-border) rounded-b-[10px] flex gap-2 justify-end'>
              <Button asChild variant='outline'>
                <Link href='/projects'>
                  キャンセル
                </Link>
              </Button>
              <Button type='submit' disabled={processing}>
                作成する
              </Button>
            </div>
          </div>
        </form>
      </PageContainer>
    </AppLayout>
  )
}
